import * as actions from 'bc/store/actions';
import {composeReducers, reduceArrayItem} from 'common/utils/reducers';
import {isEmpty} from 'lodash';
import {getUniqueId} from 'common/utils/guid';

const sqlDataStreamReducer = composeReducers((state, {type, payload}) => {
  const getSelectedIndex = (id = state.selectedItemId) => state.streams.data.findIndex((item) => item.id === id);

  const red = (item, itemPayload) => ({...item, ...itemPayload});

  const redWrapper = (_payload, index = getSelectedIndex()) => ({
    ...state,
    streams: {
      ...state.streams,
      data: reduceArrayItem(red, state.streams.data, index, _payload || payload),
    },
  });

  const getSchemaColType = (tableType) => {
    switch (tableType) {
      case 'int':
      case 'integer':
      case 'number':
      case 'metric':
        return 'metric';
      case 'timestamp':
        return 'timestamp';
      case 'other':
      case 'dimension':
        return 'dimension';
      default:
        return '';
    }
  };

  // gets sources ids used by items recursively
  const getSourceIdsArr = (sourcesIdArr, item) => {
    if (item.sourceColumn) {
      if (!sourcesIdArr.includes(item.sourceColumn)) {
        sourcesIdArr.push(item.sourceColumn);
      }
    } else if (item.transform && item.transform.input) {
      item.transform.input.forEach((a) => {
        getSourceIdsArr(sourcesIdArr, a);
      });
    }
    return sourcesIdArr;
  };

  const getOriginalName = (item) => {
    if (item.transform && item.transform.input) {
      return getSourceIdsArr([], item)[0].name;
    }
    if (item.sourceColumn) {
      return item.sourceColumn;
    }
    return item.name;
  };

  // looks for fileSchema item in the schema
  const findItemInSchema = (columns, item) =>
    columns.find(
      (a) =>
        a.sourceColumn === item.name ||
        (a.transform && a.transform.input && a.transform.input.length === 1 && item.name === getSourceIdsArr([], a)[0]),
    );

  const isItemTypeTime = (stream, item) => stream.timestampColumn && item.name === stream.timestampColumn;

  const getIsItemSourceMerged = (unifiedSchema, itemId, itemName) =>
    unifiedSchema.find(
      (a) =>
        ((a.sourceColumn && a.sourceColumn.toString() === itemId.toString()) ||
          (getSourceIdsArr([], a)[0] && getSourceIdsArr([], a)[0].toString() === itemId.toString())) &&
        a.name !== itemName &&
        a.isMerged,
    );

  const updateItemNoMatch = (stream, item) => {
    const itemMatch = {...item};
    if (isItemTypeTime(stream, item)) {
      itemMatch.type = 'timestamp';
    }
    return itemMatch;
  };

  const updateItemMatch = (stream, item) => {
    let itemMatch = {...item};
    if (isItemTypeTime(stream, item)) {
      itemMatch.type = 'timestamp';
    } else if (stream.metrics.includes(item.name) || stream.dimensions.includes(item.name)) {
      itemMatch = {
        ...itemMatch,
        ...findItemInSchema(stream.schema && stream.schema.columns ? stream.schema.columns : [], item),
        isMerged: true,
      };
    }
    return itemMatch;
  };

  switch (type) {
    case actions.setSqlStreamTableName.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      const payloadInner = {
        ...payload,
        uiState: {
          ...stream.uiState,
          editEnabled: true,
          selectedRadio: 'table',
        },
      };
      return {...redWrapper(payloadInner), isStreamNeedUpdating: true};
    }

    case actions.setSqlStreamSchemaName.TYPE: {
      const payloadInner = {
        ...payload,
        metrics: [],
        dimensions: [],
        timestampColumn: null,
        timestampType: null,
        uiState: {
          unAssignedColumns: [],
          selectedRadio: 'table',
        },
      };
      return {...redWrapper(payloadInner), isStreamNeedUpdating: true};
    }

    case actions.setIsStreamEditEnabled.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      const payloadInner = {
        uiState: {
          ...stream.uiState,
          editEnabled: payload,
        },
      };
      return {...redWrapper(payloadInner), isStreamNeedUpdating: true};
    }

    case actions.setSqlTableData.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];

      let unifiedSchema = [];
      let timeItem = {};
      let itemMatch = {};
      const tablesViewsMetadataArr = [];

      // compare and merge the payload and current fileSchema
      if (stream.uiState && stream.uiState.tablesViewsMetadata) {
        // old analyze data available
        payload.forEach((newItem) => {
          itemMatch = {};
          // loop the old file schema
          stream.uiState.tablesViewsMetadata.forEach((oldItem) => {
            // look for item match in the old analyze result
            // (note: old item type might not be available in old streams)
            if (newItem.name === oldItem.name && newItem.type === oldItem.type) {
              itemMatch = updateItemMatch(stream, newItem);
              if (itemMatch.type !== 'timestamp') {
                if (
                  !stream.metrics.includes(getOriginalName(itemMatch)) &&
                  !stream.dimensions.includes(getOriginalName(itemMatch))
                ) {
                  itemMatch.type = '';
                }
              }
            }
          });
          if (isEmpty(itemMatch)) {
            itemMatch = updateItemNoMatch(stream, newItem);
          }
          unifiedSchema.push({...itemMatch});
        });
      } else if (stream.metrics.length || stream.dimensions.length) {
        // no uiState and running stream
        payload.forEach((newItem) => {
          itemMatch = updateItemMatch(stream, newItem);
          unifiedSchema.push({...itemMatch});
        });
      } else {
        // no uiState and no dimensions, metrics and schema
        unifiedSchema = [...payload];
      }

      // add FE date/number possible types to relevant items
      payload.forEach((item) => {
        const tempItem = {
          name: item.name,
          id: item.name,
          type: item.type,
        };
        if (['number', 'integer', 'int'].indexOf(item.type) !== -1) {
          // eslint-disable-next-line no-param-reassign
          tempItem.possibleTypes = ['number', 'timestamp'];
        }
        tablesViewsMetadataArr.push(tempItem);
      });

      // get missing time definition
      if (isEmpty(timeItem)) {
        const tempTime = unifiedSchema.find((item) => item.type === 'timestamp');
        if (tempTime !== undefined) {
          timeItem = tempTime;
        }
      }

      // extract the updated unifiedSchema
      const propsToUpdate = {
        uiState: {
          ...stream.uiState,
          customQuery: false,
          tablesViewsMetadata: tablesViewsMetadataArr,
          unAssignedColumns: unifiedSchema
            .filter(
              (item) => (!item.type || getSchemaColType(item.type) === 'timestamp') && item.name !== timeItem.name,
            )
            .map((item) => item.name),
        },
        dimensions: unifiedSchema
          .filter((item) => getSchemaColType(item.type) === 'dimension')
          .map((item) => getOriginalName(item)),
        metrics: unifiedSchema
          .filter((item) => getSchemaColType(item.type) === 'metric')
          .map((item) => getOriginalName(item)),
        timestampColumn: timeItem.name,
        timestampType: timeItem.type === 'timestamp' ? 'timestamp' : 'epoch_seconds',
        schema: {
          columns: [],
          sourceColumns: [],
        },
      };

      // schema population
      let unifiedItem = {};
      propsToUpdate.metrics.forEach((itemName) => {
        const metric = unifiedSchema.find((a) => getOriginalName(a) === itemName);
        unifiedItem = {
          id: metric.id || getUniqueId(),
          name: metric.name,
          type: 'metric',
        };
        if (metric.sourceColumn) {
          unifiedItem.sourceColumn = metric.sourceColumn;
        }
        if (metric.transform) {
          unifiedItem.transform = {...metric.transform};
        }
        if (!metric.sourceColumn && !metric.transform) {
          unifiedItem.sourceColumn = metric.name;
        }
        if (metric.targetType) {
          unifiedItem.targetType = metric.targetType;
        }
        if (metric.hidden !== undefined) {
          unifiedItem.hidden = metric.hidden;
        }
        const originalName = getOriginalName(unifiedItem);
        propsToUpdate.schema.columns.push(unifiedItem);
        let exsistInSource = false;
        propsToUpdate.schema.sourceColumns.forEach((sourceItem) => {
          if (sourceItem.id === originalName) {
            exsistInSource = true;
          }
        });
        if (!exsistInSource) {
          propsToUpdate.schema.sourceColumns.push({
            id: originalName,
            name: originalName,
          });
        }
      });
      propsToUpdate.dimensions.forEach((itemName) => {
        const dimension = unifiedSchema.find((a) => getOriginalName(a) === itemName);
        unifiedItem = {
          id: dimension.id || getUniqueId(),
          name: dimension.name,
          type: 'dimension',
        };
        if (dimension.sourceColumn) {
          unifiedItem.sourceColumn = dimension.sourceColumn;
        }
        if (dimension.transform) {
          unifiedItem.transform = {...dimension.transform};
        }
        if (!dimension.sourceColumn && !dimension.transform) {
          unifiedItem.sourceColumn = dimension.name;
        }
        if (dimension.hidden !== undefined) {
          unifiedItem.hidden = dimension.hidden;
        }
        const originalName = getOriginalName(unifiedItem);
        propsToUpdate.schema.columns.push(unifiedItem);
        let exsistInSource = false;
        propsToUpdate.schema.sourceColumns.forEach((sourceItem) => {
          if (sourceItem.id === originalName) {
            exsistInSource = true;
          }
        });
        if (!exsistInSource) {
          propsToUpdate.schema.sourceColumns.push({
            id: originalName,
            name: originalName,
          });
        }
      });

      // get a list of all the current indexes
      const existingIndexArr = [...propsToUpdate.dimensions, ...propsToUpdate.metrics];

      // compare and copy the user created columns from the current schema
      if (stream.schema && stream.schema.columns) {
        stream.schema.columns.forEach((item) => {
          if (
            item.sourceColumn !== undefined ||
            (item.transform &&
              item.transform.input &&
              item.transform.input.length === 1 &&
              getSourceIdsArr([], item).length === 1)
          ) {
            const sourceColumn = item.sourceColumn || getSourceIdsArr([], item)[0];
            if (
              existingIndexArr.includes(parseInt(sourceColumn, 10)) &&
              getIsItemSourceMerged(unifiedSchema, sourceColumn, item.name)
            ) {
              // user added columns that are a copy of existing column
              propsToUpdate.schema.columns.push({...item});
            }
          } else if (item.transform && item.transform.name === 'const') {
            // user added const column
            propsToUpdate.schema.columns.push({...item});
          } else if (item.transform && item.transform.input && item.transform.input.length > 1) {
            // user added concat columns
            let shouldPush = true;
            getSourceIdsArr([], item).forEach((id) => {
              if (!existingIndexArr.includes(id) || getIsItemSourceMerged(unifiedSchema, id, item.name) === undefined) {
                shouldPush = false;
              }
            });
            if (shouldPush) {
              propsToUpdate.schema.columns.push({...item});
            }
          }
        });
      }

      return {...redWrapper(propsToUpdate), isStreamNeedUpdating: true};
    }

    case actions.setSqlStreamClearAllDiametricsAndUnAssigned.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      const mod = {
        metrics: [],
        dimensions: [],
        timestampColumn: null,
        timestampType: null,
        uiState: {
          ...stream.uiState,
          unAssignedColumns: [],
        },
      };
      return redWrapper(mod);
    }

    case actions.setSqlStreamClearAllDiametrics.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      const meta = stream.uiState.tablesViewsMetadata;
      const mod = {
        metrics: [],
        dimensions: [],
        timestampColumn: null,
        uiState: {
          ...stream.uiState,
          unAssignedColumns: meta.map((a) => a.name),
        },
      };
      return redWrapper(mod);
    }

    case actions.removeSqlStreamDiametrics.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      let updateSchema = false;
      const mod = {};
      if (stream.metrics.indexOf(payload) >= 0) {
        updateSchema = true;
        mod.metrics = stream.metrics.filter((a) => a !== payload);
      } else if (stream.dimensions.indexOf(payload) >= 0) {
        updateSchema = true;
        mod.dimensions = stream.dimensions.filter((a) => a !== payload);
      } else if (stream.timestampColumn === payload) {
        updateSchema = true;
        mod.timestampColumn = null;
        mod.timestampType = null;
      }
      mod.uiState = {
        ...stream.uiState,
        unAssignedColumns: [...stream.uiState.unAssignedColumns, payload],
      };
      if (updateSchema) {
        mod.schema = {
          columns: stream.schema.columns.filter((a) => {
            if (a.sourceColumn) {
              return a.sourceColumn !== payload;
            }
            return !getSourceIdsArr([], a).includes(payload);
          }),
          sourceColumns: stream.schema.sourceColumns.filter((a) => a.name !== payload),
        };
      }
      return redWrapper(mod);
    }

    case actions.setSqlStreamDiametricsChange.TYPE: {
      const stream = state.streams.data[getSelectedIndex()];
      const streamModifications = {
        uiState: {...stream.uiState},
      };
      let allowed = true;

      const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
      };

      if (payload.source.droppableId === payload.destination.droppableId) {
        switch (payload.destination.droppableId) {
          case 'dmAllColumns': {
            streamModifications.uiState.unAssignedColumns = reorder(
              stream.uiState.unAssignedColumns,
              payload.source.index,
              payload.destination.index,
            );
            break;
          }
          case 'dmMetrics': {
            streamModifications.metrics = reorder(stream.metrics, payload.source.index, payload.destination.index);
            break;
          }
          case 'dmDimensions': {
            streamModifications.dimensions = reorder(
              stream.dimensions,
              payload.source.index,
              payload.destination.index,
            );
            break;
          }
          case 'dmDate': {
            break;
          }
          default:
            return state;
        }
      } else {
        const tableMeta = stream.uiState.tablesViewsMetadata;
        switch (payload.destination.droppableId) {
          case 'dmAllColumns': {
            streamModifications.uiState.unAssignedColumns = [...stream.uiState.unAssignedColumns];
            streamModifications.uiState.unAssignedColumns.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmMetrics': {
            streamModifications.metrics = [...stream.metrics];
            streamModifications.metrics.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmDimensions': {
            streamModifications.dimensions = [...stream.dimensions];
            streamModifications.dimensions.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmDate': {
            const item = tableMeta.find((a) => a.name === payload.draggableId);
            if (['timestamp', 'int', 'integer'].indexOf(item.type) !== -1) {
              if (stream.timestampColumn) {
                streamModifications.uiState.unAssignedColumns = [...stream.uiState.unAssignedColumns];
                streamModifications.uiState.unAssignedColumns.splice(0, 0, stream.timestampColumn);
              }
              streamModifications.timestampColumn = item.name;
              streamModifications.timestampType = item.type === 'timestamp' ? 'timestamp' : 'epoch_seconds';
            } else {
              allowed = false;
            }
            break;
          }
          default:
            return state;
        }

        if (allowed) {
          switch (payload.source.droppableId) {
            case 'dmAllColumns': {
              streamModifications.uiState.unAssignedColumns = [
                ...(streamModifications.uiState.unAssignedColumns || stream.uiState.unAssignedColumns),
              ];
              streamModifications.uiState.unAssignedColumns.splice(
                streamModifications.uiState.unAssignedColumns.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmMetrics': {
              streamModifications.metrics = [...stream.metrics];
              streamModifications.metrics.splice(
                streamModifications.metrics.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmDimensions': {
              streamModifications.dimensions = [...stream.dimensions];
              streamModifications.dimensions.splice(
                streamModifications.dimensions.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmDate': {
              streamModifications.timestampColumn = null;
              streamModifications.timestampType = null;
              break;
            }
            default:
              return state;
          }
        }
      }
      return redWrapper(streamModifications);
    }

    default:
      return state;
  }
});

export default sqlDataStreamReducer;
